import React, { useState, useEffect, useRef } from 'react';
import ProgramModal from '../../components/community/modals/ProgramModal';
import StatsBoxes from '../../components/community/cards/StatsCard';
import ServiceImpactSummary from '../../components/community/ServiceImpactSummary';
import ProgramFilter from '../../components/community/ProgramFilter';
import ReportingModal from '../../components/community/modals/ReportingModal';
import ManageCategoryModal from '../../components/community/modals/ManageCategoryModal';
import ProgramsTable from '../../components/community/ProgramsTable';
import { communityServicesAPI } from '../../services/api';
import { FolderIcon, UserGroupIcon, DocumentTextIcon, PlusIcon } from '@heroicons/react/24/outline';
import VolunteerRegistrationManagement from '../../components/community/modals/VolunteerRegistrationManagement';

function CommunityServicesPage() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showNewProgramModal, setShowNewProgramModal] = useState(false);
  const [showReportingModal, setShowReportingModal] = useState(false);
  const [showManageCategoryModal, setShowManageCategoryModal] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [reportingDropdownOpen, setReportingDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showVolunteerModal, setShowVolunteerModal] = useState(false);
  const [volunteerToEdit, setVolunteerToEdit] = useState(null);

  const [filters, setFilters] = useState({
    status: '',
    start_date: '',
    end_date: '',
    search: ''
  });

  const [counts, setCounts] = useState({
    scheduled: 0,
    volunteers: 0,
    attendance: 0
  });

  const [serviceStats, setServiceStats] = useState({
    volunteersParticipated: 0,
    serviceHours: 0,
    programsCompleted: 0,
    upcomingPrograms: 0,
    upcomingProgramsHours: 0,
    upcomingProgramVolunteers: 0
  });

  const refreshTableRef = useRef(() => { });

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await communityServicesAPI.getDashboard();
      console.log("STATS IN COMMS", response)

      if (response.data.success) {
        const dashboardData = response.data.data.community_service_dashboard_view;

        setCounts({
          scheduled: dashboardData.total_scheduled_programs || 0,
          volunteers: dashboardData.total_volunteers || 0,
          attendance: dashboardData.total_attendance || 0
        });

        setServiceStats({
          volunteersParticipated: dashboardData.total_volunteers_participated || 0,
          serviceHours: dashboardData.total_service_hours_rendered_mock || 0,
          programsCompleted: dashboardData.programs_completed || 0,
          upcomingPrograms: dashboardData.upcoming_programs || 0,
          upcomingProgramsHours: dashboardData.upcoming_program_hours_mock || 0,
          upcomingProgramVolunteers: dashboardData.upcoming_program_volunteers || 0
        });
      }
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleRefreshData = async () => {
    console.log('Refreshing data...');
    await fetchDashboardData();

    if (refreshTableRef.current) {
      refreshTableRef.current();
    }
  };

  const handleFiltersUpdate = (updatedFilters) => {
    setFilters(updatedFilters);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  const toggleReportingDropdown = () => {
    setReportingDropdownOpen(!reportingDropdownOpen);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      start_date: '',
      end_date: '',
      search: ''
    });
  };

  const handleApplyFilters = () => {
    setShowFilter(false);

  };

  const handleReportingOptionClick = (option) => {
    setReportingDropdownOpen(false);
    if (option === 'reporting') {
      setShowReportingModal(true);
    } else if (option === 'manage-category') {
      setShowManageCategoryModal(true);
    }
    else if (option === 'volunteer-registration') {
      setShowVolunteerModal(true);
    }
  };

  const statConfigs = [
    {
      key: 'scheduled',
      title: 'Total Scheduled Programs',
      subtitle: 'Upcoming and ongoing activities',
      bgColor: '#DCEBFE',
      textColor: '#2563EB'
    },
    {
      key: 'volunteers',
      title: 'Total Volunteers Registered',
      subtitle: 'Enrolled community members',
      bgColor: '#CCFBF1',
      textColor: '#0D9488'
    },
    {
      key: 'attendance',
      title: 'Total Attendance Count',
      subtitle: 'Recorded participations',
      bgColor: '#DCFCE7',
      textColor: '#16A34F'
    }
  ];

  if (loading) {
    return (
      <div className="max-w-12xl mx-auto px-4 sm:px-2 lg:px-2 py-2">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading dashboard data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-12xl mx-auto px-4 sm:px-2 lg:px-2 py-2">
        <div className="flex justify-center items-center h-64">
          <div className="text-red-600 text-lg">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-12xl mx-auto px-2 sm:px-2 lg:px-2 py-2">

      <div className="mb-8 sm:mb-4">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 text-center sm:text-left">
          <div
            className="flex items-center justify-center min-w-[50px] sm:min-w-[60px] h-12 sm:h-14 rounded-lg"
            style={{ backgroundColor: '#DCEBFE' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="w-6 h-6 sm:w-7 sm:h-7"
            >
              <path
                fill="none"
                stroke="#2563EB"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
              />
            </svg>
          </div>

          <div className="flex-1">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <h6 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                  Community Services
                </h6>
                <p className="text-gray-600 text-xs sm:text-sm tracking-wider mt-1">
                  Manage programs with volunteers and impact tracking
                </p>
              </div>

              <div className="flex gap-2">
                {/* Reporting Dropdown */}
                <div className="relative">
                  <button
                    onClick={toggleReportingDropdown}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg transition-colors whitespace-nowrap text-sm font-medium"
                    style={{ backgroundColor: '#DBEAFE', color: '#1E3A8A' }}
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                        style={{ color: '#2563EB' }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
                      </svg>
                      Manage Category
                    </div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className={`w-4 h-4 transition-transform ${reportingDropdownOpen ? 'rotate-180' : ''}`}
                    >
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {reportingDropdownOpen && (
                    <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-10 min-w-[220px] text-sm">
                      <button
                        onClick={() => handleReportingOptionClick('manage-category')}
                        className="flex items-center gap-2 w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-t-lg"
                      >
                        <FolderIcon className="w-4 h-4 text-blue-600" />
                        Manage Category
                      </button>
                      <button
                        onClick={() => handleReportingOptionClick('volunteer-registration')}
                        className="flex items-center gap-2 w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-50"
                      >
                        <UserGroupIcon className="w-4 h-4 text-blue-600" />
                        Volunteer Registration
                      </button>
                      <button
                        onClick={() => handleReportingOptionClick('reporting')}
                        className="flex items-center gap-2 w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-b-lg"
                      >
                        <DocumentTextIcon className="w-4 h-4 text-blue-600" />
                        Reporting
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setShowNewProgramModal(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <PlusIcon className="w-4 h-4" />
                  New Program
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StatsBoxes counts={counts} statConfigs={statConfigs} />

      <ServiceImpactSummary
        serviceStats={serviceStats}
        isExpanded={isExpanded}
        toggleExpand={toggleExpand}
      />

      <ProgramsTable
        ref={refreshTableRef}
        filters={filters}
        onRefreshNeeded={handleFiltersUpdate}
        showFilter={showFilter}
        toggleFilter={toggleFilter}
        onFilterChange={(updatedFilters) => setFilters(updatedFilters)}
        onClearFilters={handleClearFilters}
        onApplyFilters={handleApplyFilters}
      />

      <ProgramModal
        isOpen={showNewProgramModal}
        onClose={() => setShowNewProgramModal(false)}
        mode="create"
        onSuccess={() => {
          setShowNewProgramModal(false);
          handleRefreshData();
        }}
      />

      <ReportingModal
        isOpen={showReportingModal}
        onClose={() => setShowReportingModal(false)}
      />

      <VolunteerRegistrationManagement
        isOpen={showVolunteerModal}
        onClose={() => setShowVolunteerModal(false)}
        onSuccess={handleRefreshData}
        volunteerToEdit={volunteerToEdit}
      />

      <ManageCategoryModal
        isOpen={showManageCategoryModal}
        onClose={() => setShowManageCategoryModal(false)}
      />
    </div>
  );
}

export default CommunityServicesPage;